import { AlertToastComponent } from '@/core/components/alert-toast/alert-toast.component';
import { AuthenticationService } from '@/core/services/authentication/authentication.service';
import {
  FetchLastNotifications,
  FetchMeUser,
  FetchServicesPrice,
} from '@/modules/home/core/state/home.actions';
import {
  afterNextRender,
  Component,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { Store } from '@ngxs/store';
import { PrimeNG } from 'primeng/config';
import { filter, skip, Subject, takeUntil } from 'rxjs';
import { isPublicAuthRoute } from './auth/shared/public-auth-routes';
import { FirebaseConfigService } from './FirebaseConfig/firebaseConfig.service';
import { AppConfiguratorService } from './layout/components/app.configurator.service';
import { DeviceService } from './services/device.service';
import { NotificationPermissionMonitor } from './services/notification-permission-monitor.service';
import { NotificationPermissionComponent } from './shared/components/notification-permission/notification-permission.component';
import { ContrastService } from './shared/services/contrast.service';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, AlertToastComponent, NotificationPermissionComponent],
  standalone: true,
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly authService = inject(AuthenticationService);
  private permissionMonitor = inject(NotificationPermissionMonitor);
  private notificationService = inject(AppConfiguratorService);

  private readonly store = inject(Store);
  private readonly firebaseConfigService = inject(FirebaseConfigService);
  private readonly deviceService = inject(DeviceService);
  private readonly router = inject(Router);

  private destroy$ = new Subject<void>();
  private readonly contrastService = inject(ContrastService);
  protected showNotificationPopup =
    this.notificationService.showNotificationPopup;

  constructor(private readonly config: PrimeNG) {
    afterNextRender(() => {
      this.contrastService.init();
    });

    this.config.setTranslation({
      dayNames: [
        'Domingo',
        'Segunda',
        'Terça',
        'Quarta',
        'Quinta',
        'Sexta',
        'Sábado',
      ],
      dayNamesShort: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'],
      dayNamesMin: ['Do', 'Se', 'Te', 'Qu', 'Qu', 'Se', 'Sa'],
      monthNames: [
        'Janeiro',
        'Fevereiro',
        'Março',
        'Abril',
        'Maio',
        'Junho',
        'Julho',
        'Agosto',
        'Setembro',
        'Outubro',
        'Novembro',
        'Dezembro',
      ],
      monthNamesShort: [
        'Jan',
        'Fev',
        'Mar',
        'Abr',
        'Mai',
        'Jun',
        'Jul',
        'Ago',
        'Set',
        'Out',
        'Nov',
        'Dez',
      ],
      today: 'Hoje',
      clear: 'Limpar',
      dateFormat: 'dd/mm/yy',
      weekHeader: 'Sm',
    });
  }

  ngOnInit() {
    const isConfirmRoute = isPublicAuthRoute(window.location.pathname);

    if (!isConfirmRoute) {
      this.logoutAllTabs();
    }

    this.authService.isAuth().subscribe({
      next: async (isAuth) => {
        if (isAuth) {
          this.store.dispatch(new FetchMeUser());
          this.store.dispatch(new FetchServicesPrice());
          this.store.dispatch(new FetchLastNotifications());
        }

        if (this.authService.isLogged()) {
          setTimeout(() => {
            this.initializeNotificationsSafely();
          }, 1000);
        }
      },
    });

    if (!isConfirmRoute) {
      this.router.events
        .pipe(
          filter((e) => e instanceof NavigationEnd),
          takeUntil(this.destroy$),
        )
        .subscribe(async () => {
          await this.notificationService.checkNotificationStatus();
        });
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();

    this.permissionMonitor.stopMonitoring();
  }

  private async initializeNotificationsSafely(): Promise<void> {
    try {
      const notificationResult = await this.deviceService.enableNotifications();

      if (!notificationResult.success) {
        console.warn(
          '[FCM] Notificações não habilitadas:',
          notificationResult.reason,
        );
      }

      this.permissionMonitor.startMonitoring();
    } catch (error) {
      console.warn('[FCM] Falha ignorada para não travar renderização:', error);
    }
  }

  private logoutAllTabs(): void {
    this.authService.token$
      .pipe(skip(1), takeUntil(this.destroy$))
      .subscribe((token) => {
        if (!token) {
          this.onTokenRemoved();
        }
      });
  }

  private onTokenRemoved(): void {
    if (!this.router.url.includes('/login')) {
      this.router.navigate(['/login'], {
        queryParamsHandling: 'preserve',
      });
    }
  }
}
